
import { GeolocationOptions } from './types';
import { getGeolocationErrorMessage } from './errors';
import { isSSR } from '../isSSR';
import { toast } from 'sonner';
import { debugLog, debugError, debugWarn } from '../../utils/debugLogger';
import { getCachedPosition, cachePosition } from './positionCache';

// Check if geolocation is supported by the browser
export const isGeolocationSupported = (): boolean => {
  // In SSR, geolocation is never available
  if (isSSR()) {
    return false;
  }
  
  return !!navigator && !!navigator.geolocation;
};

// Enhanced two-phase geolocation strategy with intelligent timeout handling
export const getCurrentPositionOptimized = async (options: GeolocationOptions = {}): Promise<GeolocationPosition> => {
  const {
    forceRefresh = false,
    enableHighAccuracy = true,
    timeout = enableHighAccuracy ? 20000 : 10000, // Reduced timeouts: 20s for high accuracy, 10s for low
    maximumAge = forceRefresh ? 0 : 120000,
    onSuccess,
    onError,
    showSuccessToast = false,
    useCache = true,
    silentBackground = false
  } = options;

  // Skip cache if forceRefresh is true
  if (useCache && !forceRefresh) {
    const cachedPosition = getCachedPosition();
    if (cachedPosition) {
      debugLog('general', 'Using cached position instead of new GPS request', undefined, 'getCurrentPositionOptimized');
      if (showSuccessToast && !silentBackground) {
        toast.success("Posizione rilevata (cache)");
      }
      if (onSuccess) onSuccess(cachedPosition);
      return cachedPosition;
    }
  }

  // Log when we're forcing a refresh
  if (forceRefresh) {
    debugLog('general', 'Force refresh requested - bypassing cache and requesting new GPS position', undefined, 'getCurrentPositionOptimized');
  }

  // Show progress for longer operations
  if (!silentBackground && enableHighAccuracy) {
    toast.loading("Rilevamento posizione precisa in corso...", { id: 'geolocation-progress' });
  }

  // Phase 1: Quick low-accuracy attempt (5 seconds)
  try {
    debugLog('general', 'Phase 1: Attempting quick low-accuracy geolocation', undefined, 'getCurrentPositionOptimized');
    
    const quickPosition = await getCurrentPositionPhase({
      enableHighAccuracy: false,
      timeout: 5000, // Reduced timeout for faster fallback
      maximumAge: forceRefresh ? 0 : maximumAge,
      onSuccess,
      onError,
      showSuccessToast: false
    });

    // Dismiss loading toast
    if (!silentBackground) {
      toast.dismiss('geolocation-progress');
    }

    // If we got a reasonably accurate position, use it
    if (quickPosition.coords.accuracy <= 100) {
      debugLog('general', `Phase 1 success: accuracy ${Math.round(quickPosition.coords.accuracy)}m`, undefined, 'getCurrentPositionOptimized');
      
      // Cache the position
      cachePosition(quickPosition);
      
      if (showSuccessToast && !silentBackground) {
        toast.success("Posizione rilevata rapidamente");
      }
      if (onSuccess) onSuccess(quickPosition);
      return quickPosition;
    }

    // If accuracy is poor but we want high accuracy, try phase 2
    if (enableHighAccuracy) {
      debugLog('general', `Phase 1 completed with poor accuracy (${Math.round(quickPosition.coords.accuracy)}m), trying phase 2`, undefined, 'getCurrentPositionOptimized');
      
      // Show progress for phase 2
      if (!silentBackground) {
        toast.loading("Cercando posizione più precisa...", { id: 'geolocation-progress-2' });
      }
      
      try {
        const accuratePosition = await getCurrentPositionPhase({
          enableHighAccuracy: true,
          timeout: Math.max(timeout - 5000, 12000), // Remaining time, min 12s
          maximumAge: 0, // Force fresh position
          onSuccess,
          onError,
          showSuccessToast: false
        });

        // Dismiss loading toast
        if (!silentBackground) {
          toast.dismiss('geolocation-progress-2');
        }

        debugLog('general', `Phase 2 success: accuracy ${Math.round(accuratePosition.coords.accuracy)}m`, undefined, 'getCurrentPositionOptimized');
        
        // Cache the accurate position
        cachePosition(accuratePosition);
        
        if (showSuccessToast && !silentBackground) {
          toast.success("Posizione precisa rilevata");
        }
        if (onSuccess) onSuccess(accuratePosition);
        return accuratePosition;
      } catch (phase2Error) {
        // Dismiss loading toast
        if (!silentBackground) {
          toast.dismiss('geolocation-progress-2');
        }

        // Phase 2 failed, use phase 1 result
        debugLog('general', 'Phase 2 failed, using phase 1 result', phase2Error, 'getCurrentPositionOptimized');
        
        // Cache the quick position
        cachePosition(quickPosition);
        
        if (showSuccessToast && !silentBackground) {
          toast.success("Posizione rilevata (approssimativa)");
        }
        if (onSuccess) onSuccess(quickPosition);
        return quickPosition;
      }
    } else {
      // High accuracy not requested, use quick result
      // Cache the quick position
      cachePosition(quickPosition);
      
      if (showSuccessToast && !silentBackground) {
        toast.success("Posizione rilevata");
      }
      if (onSuccess) onSuccess(quickPosition);
      return quickPosition;
    }
  } catch (phase1Error) {
    // Dismiss loading toast if phase 1 failed
    if (!silentBackground) {
      toast.dismiss('geolocation-progress');
    }

    // Phase 1 failed, try phase 2 if high accuracy was requested
    if (enableHighAccuracy) {
      debugLog('general', 'Phase 1 failed, trying phase 2', phase1Error, 'getCurrentPositionOptimized');
      
      // Show progress for direct phase 2
      if (!silentBackground) {
        toast.loading("Tentativo con GPS ad alta precisione...", { id: 'geolocation-progress-fallback' });
      }
      
      try {
        const accuratePosition = await getCurrentPositionPhase({
          enableHighAccuracy: true,
          timeout: timeout, // Use full timeout for fallback
          maximumAge: forceRefresh ? 0 : maximumAge,
          onSuccess,
          onError,
          showSuccessToast: false
        });

        // Dismiss loading toast
        if (!silentBackground) {
          toast.dismiss('geolocation-progress-fallback');
        }

        debugLog('general', `Phase 2 success after phase 1 failure: accuracy ${Math.round(accuratePosition.coords.accuracy)}m`, undefined, 'getCurrentPositionOptimized');
        
        // Cache the position
        cachePosition(accuratePosition);
        
        if (showSuccessToast && !silentBackground) {
          toast.success("Posizione rilevata con GPS");
        }
        if (onSuccess) onSuccess(accuratePosition);
        return accuratePosition;
      } catch (phase2Error) {
        // Dismiss loading toast
        if (!silentBackground) {
          toast.dismiss('geolocation-progress-fallback');
        }

        // Both phases failed - warning perché l'errore viene già gestito con toast
        debugWarn('general', 'Both geolocation phases failed', { phase1Error, phase2Error }, 'getCurrentPositionOptimized');
        
        // Show specific error handling
        if (showSuccessToast && !silentBackground) {
          handleGeolocationError(phase2Error as GeolocationPositionError, options);
        }
        
        if (onError) onError(phase2Error as GeolocationPositionError);
        throw phase2Error;
      }
    } else {
      // Only phase 1 was attempted and it failed
      debugWarn('general', 'Phase 1 failed and high accuracy not requested', phase1Error, 'getCurrentPositionOptimized');
      
      if (showSuccessToast && !silentBackground) {
        handleGeolocationError(phase1Error as GeolocationPositionError, options);
      }
      
      if (onError) onError(phase1Error as GeolocationPositionError);
      throw phase1Error;
    }
  }
};

// Single-phase geolocation function with intelligent timeout adjustment
const getCurrentPositionPhase = (options: GeolocationOptions = {}): Promise<GeolocationPosition> => {
  const {
    enableHighAccuracy = true, 
    timeout = enableHighAccuracy ? 20000 : 10000, // Optimized timeouts: 20s/10s
    maximumAge = 120000,
    onSuccess,
    onError,
    showSuccessToast = false
  } = options;
  
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      const error = new Error("La geolocalizzazione non è supportata dal tuo browser");
      if (onError) onError(error as unknown as GeolocationPositionError);
      reject(error);
      return;
    }
    
    // Create a timeout ID for manual handling
    let timeoutId: number | null = null;
    
    // Define success handler
    const handleSuccess = (position: GeolocationPosition) => {
      // Clear timeout if it exists
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      
      debugLog('general', `Position received: ${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)} (accuracy: ${Math.round(position.coords.accuracy)}m)`, undefined, 'getCurrentPositionPhase');
      
      if (onSuccess) onSuccess(position);
      resolve(position);
    };
    
    // Define error handler
    const handleError = (error: GeolocationPositionError) => {
      // Clear timeout if it exists
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      
      const formattedError = getGeolocationErrorMessage(error);
      debugWarn('general', "Errore geolocalizzazione:", {formattedError, code: error.code}, 'getCurrentPositionPhase');
      
      if (onError) onError(error);
      reject(error);
    };
    
    // Check for low-power mode or poor signal
    const connection = (navigator as any).connection;
    const lowPowerMode = (navigator as any).powerSavingEnabled;
    
    // Adjust accuracy based on device conditions
    let finalEnableHighAccuracy = enableHighAccuracy;
    let finalTimeout = timeout;
    
    if (connection && (connection.saveData || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')) {
      // If on slow connection, use lower accuracy and slightly longer timeout
      finalEnableHighAccuracy = false;
      finalTimeout = Math.min(timeout * 1.5, 25000); // Max 25s for slow connections
      debugLog('general', "Slow connection detected, adjusting geolocation settings", undefined, 'getCurrentPositionPhase');
    }
    
    if (lowPowerMode) {
      // If in power saving mode, use lower accuracy
      finalEnableHighAccuracy = false;
      debugLog('general', "Power saving mode detected, using lower accuracy for geolocation", undefined, 'getCurrentPositionPhase');
    }
    
    // Log attempt
    debugLog('general', `Requesting position (highAccuracy: ${finalEnableHighAccuracy}, timeout: ${finalTimeout}ms, maximumAge: ${maximumAge}ms)`, undefined, 'getCurrentPositionPhase');
    
    try {
      // Start geolocation request
      navigator.geolocation.getCurrentPosition(
        handleSuccess,
        handleError,
        { enableHighAccuracy: finalEnableHighAccuracy, timeout: finalTimeout, maximumAge }
      );
      
      // Set a manual timeout as a fallback - minimal buffer for faster response
      timeoutId = window.setTimeout(() => {
        // Create a custom timeout error that matches GeolocationPositionError structure
        const timeoutError = {
          code: 3,
          message: "Timeout nel rilevamento della posizione",
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3
        } as GeolocationPositionError;
        
        handleError(timeoutError);
      }, finalTimeout + 2000); // Reduced buffer from 5s to 2s
    } catch (e) {
      debugError('general', "Unexpected error requesting geolocation:", e, 'getCurrentPositionPhase');
      reject(e);
    }
  });
};

// Enhanced error handling with user-friendly messages
const handleGeolocationError = (error: GeolocationPositionError, options: GeolocationOptions) => {
  const { enableHighAccuracy } = options;
  
  switch (error.code) {
    case 1: // Permission denied
      toast.error("Accesso alla posizione negato", {
        description: "Concedi i permessi nelle impostazioni del browser per vedere i compro oro più vicini",
        duration: 7000,
      });
      break;
    case 2: // Position unavailable
      toast.error("Posizione non disponibile", {
        description: "Verifica che il GPS sia attivo e che tu sia in un'area con segnale",
        action: {
          label: "Riprova",
          onClick: () => getCurrentPositionOptimized({...options, enableHighAccuracy: false, showSuccessToast: true}),
        },
        duration: 7000,
      });
      break;
    case 3: // Timeout
      toast.error("GPS non risponde", {
        description: enableHighAccuracy ? "Il GPS ad alta precisione richiede più tempo. Prova con precisione ridotta o spostati all'aperto." : "Verifica la connessione internet e riprova",
        action: {
          label: enableHighAccuracy ? "Modalità veloce" : "Riprova",
          onClick: () => getCurrentPositionOptimized({...options, enableHighAccuracy: false, timeout: 8000, showSuccessToast: true}),
        },
        duration: 10000,
      });
      break;
    default:
      toast.error("Errore di geolocalizzazione", {
        description: "Si è verificato un errore imprevisto",
        action: {
          label: "Riprova",
          onClick: () => getCurrentPositionOptimized({...options, enableHighAccuracy: false, showSuccessToast: true}),
        },
        duration: 5000,
      });
      break;
  }
};

// Backward compatibility - use optimized version by default
export const getCurrentPosition = getCurrentPositionOptimized;
